import React, { createContext, useState, useCallback } from 'react';
import { ThemeTokens } from './tokens';
import { natureTheme } from './themes/nature';
import { darkTheme } from './themes/dark';
import { lightTheme } from './themes/light';
import {
  THEME_MODE_NATURE,
  THEME_MODE_DARK,
  THEME_MODE_LIGHT,
  DEFAULT_THEME_MODE,
  THEME_STORAGE_KEY,
  AVAILABLE_THEME_MODES,
} from './constants';

export type ThemeMode = (typeof AVAILABLE_THEME_MODES)[number];

interface ThemeContextValue {
  mode: ThemeMode;
  theme: ThemeTokens;
  setTheme: (mode: ThemeMode) => void;
}

export const ThemeContext = createContext<ThemeContextValue>({
  mode: DEFAULT_THEME_MODE,
  theme: natureTheme,
  setTheme: () => {},
});

const themes: Record<ThemeMode, ThemeTokens> = {
  [THEME_MODE_NATURE]: natureTheme,
  [THEME_MODE_DARK]: darkTheme,
  [THEME_MODE_LIGHT]: lightTheme,
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    return (saved as ThemeMode) || DEFAULT_THEME_MODE;
  });

  const setTheme = useCallback((newMode: ThemeMode) => {
    setMode(newMode);
    localStorage.setItem(THEME_STORAGE_KEY, newMode);
  }, []);

  const value: ThemeContextValue = {
    mode,
    theme: themes[mode],
    setTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
